import { useEffect, useRef } from 'react';

import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import type { CompositionV3BreakdownOptions } from '@revelio/data-access';
import { FormatType } from '@revelio/replots';

import { useCompanyCompetitors } from '../../../../../shared/hooks/useCompanyCompetitors';
import { useCompanyEntityInfo } from '../../../../../shared/hooks/useCompanyEntityInfo';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type {
  PrimaryFilterId,
  PrimarySelection,
} from '../../../filters/types/filters';
import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';
import {
  type DiversityCatalogEntity,
  isDiversityCatalogSupported,
} from '../../DataProvider/catalog/diversity-catalog';
import { useDiversityCatalogData } from '../../DataProvider/catalog/useDiversityCatalogData';
import {
  type CompositionV3AnyGraphqlResponse,
  isCompositionV3FullMetrics,
} from '../../DataProvider/composition/composition-v3/composition-v3-graphql-response';
import { convertCompositionDataV3 } from '../../DataProvider/composition/composition-v3/convert-composition-data-v3';
import { useCompositionData } from '../../DataProvider/composition/useCompositionData';
import {
  type BreakdownSingleDataSet,
  DataProviderQueryStatus,
  DataType,
} from '../../DataProvider/types';
import { combineQueryStatus } from '../../DataProvider/utils/data-provider-results-utils';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import { usePageState } from '../../PageState/PageStateProvider';
import { ChartFallback } from '../ChartFallback';
import { ChartLoader } from '../ChartLoader';
import { ChartNotSupported } from '../ChartNotSupported';
import { useChart } from '../ChartProvider';
import { useChartMenuContext } from '../chart-menu/ChartMenuContext';
import {
  areSelectionsValid,
  useSegmentSelections,
} from '../ShareOfSegment/ShareOfSegment';
import { useShareOfSegmentSubfilters } from '../ShareOfSegment/useSubFilter';
import { DivergingBarCombo } from './DivergingBarCombo';
import styles from './DivergingBarCombo.module.css';
import {
  aggregateCompetitorShares,
  buildCatalogPenetrationSet,
  buildCompanyCompetitorEntities,
  buildCompanyIndustryEntities,
  buildPenetrationBreakdownSet,
  buildSubfilterNarrowingFilters,
  type CatalogSegmentDimension,
  COMPETITOR_UNAVAILABLE_ERROR,
  getFocusSelection,
  getSelectedSegmentIds,
  INDUSTRY_UNAVAILABLE_ERROR,
  isCatalogSegmentDimension,
  isDynamicEntityComparison,
  isSubfilterableDimension,
  removeOtherSegments,
  resolveComboEntities,
} from './dynamic-diverging-combo';

const DYNAMIC_COMPARISON_COMPETITOR_COUNT = 2;
const COMPETITOR_AVERAGE_LABEL = 'Competitors';

type BreakdownIdOption = keyof Pick<
  CompositionV3BreakdownOptions,
  | 'education'
  | 'ethnicity'
  | 'gender'
  | 'geography'
  | 'rics'
  | 'role'
  | 'seniority'
  | 'skill'
>;

const breakdownIdOptionByDimension: Record<
  CompositionSecondaryDimensionValues,
  BreakdownIdOption
> = {
  education: 'education',
  ethnicity: 'ethnicity',
  gender: 'gender',
  geography: 'geography',
  rics: 'rics',
  role: 'role',
  seniority: 'seniority',
  skills: 'skill',
};

const getPrimaryEntityBreakdownIds = ({
  data,
  dimension,
}: {
  data: CompositionV3AnyGraphqlResponse | undefined;
  dimension: CompositionSecondaryDimensionValues;
}): number[] | undefined => {
  if (!data) return undefined;

  const metrics = data.entities[0]?.metrics;
  if (!isCompositionV3FullMetrics(metrics)) return [];

  return Array.from(
    new Set(metrics[dimension].map((breakdown) => breakdown.id))
  ).filter((id) => id > 0);
};

export const DynamicDivergingBarCombo = ({
  primaryDimension,
  plotId,
}: {
  primaryDimension: PrimaryFilterId;
  plotId: string;
}) => {
  const { item } = useChart();
  const { pageState } = usePageState();
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const isDiversityQueryEnabled = useUnleashFlag(FeatureFlag.DiversityQuery);

  const secondaryDimension = item?.plot?.secondaryDimension as
    | CompositionSecondaryDimensionValues
    | undefined;
  const subfilterDimension = item?.plot?.divergingBarCombo?.subfilterDimension;
  const dynamic = isDynamicEntityComparison(item?.plot, primaryDimension);
  const hideOther = item?.plot?.hideOther === true;

  const selections = primaryFilter?.primarySelections ?? [];

  const focusCompany = dynamic
    ? getFocusSelection(selections, pageState.primaryEntities?.[plotId])
    : null;
  const focusCompanyRcid = focusCompany
    ? getNumericIdFromNestedId(focusCompany.id)
    : null;
  const {
    competitors,
    isLoading: isCompetitorsLoading,
    hasError: hasCompetitorsError,
  } = useCompanyCompetitors(focusCompanyRcid, {
    enabled: dynamic && focusCompanyRcid !== null,
    competitorCount: DYNAMIC_COMPARISON_COMPETITOR_COUNT,
  });
  const competitorEntities =
    dynamic && focusCompany
      ? (buildCompanyCompetitorEntities(
          focusCompany,
          competitors.map((competitor) => competitor.rcid)
        ) ?? undefined)
      : undefined;
  const hasCompetitors = (competitorEntities?.length ?? 0) > 1;
  const shouldLoadIndustry =
    dynamic &&
    focusCompanyRcid !== null &&
    !isCompetitorsLoading &&
    !hasCompetitorsError &&
    !hasCompetitors;
  const {
    entityInfo,
    isLoading: isIndustryLoading,
    isFetched: isIndustryFetched,
  } = useCompanyEntityInfo(focusCompanyRcid, {
    enabled: shouldLoadIndustry,
  });
  const industryId = Number(entityInfo?.rics_k400);
  const ricsK400 =
    Number.isFinite(industryId) && industryId > 0 ? industryId : null;
  const usesCompetitorComparison = hasCompetitors;

  const { selectedSegments } = useSegmentSelections(plotId);
  const subfilterable = isSubfilterableDimension(subfilterDimension);
  const additionalEffectiveFilters = subfilterable
    ? buildSubfilterNarrowingFilters(subfilterDimension, selectedSegments)
    : [];
  const hasActiveSubfilter = additionalEffectiveFilters.length > 0;

  const entitiesOverride = (() => {
    if (!dynamic || !focusCompany) return undefined;
    if (usesCompetitorComparison) {
      return competitorEntities;
    }
    if (ricsK400 !== null) {
      return buildCompanyIndustryEntities(focusCompany, ricsK400) ?? undefined;
    }
    return undefined;
  })();
  const hasComparisonEntities = (entitiesOverride?.length ?? 0) > 1;

  const pauseCompositionQueries =
    !secondaryDimension ||
    (dynamic &&
      (focusCompany === null ||
        isCompetitorsLoading ||
        hasCompetitorsError ||
        (shouldLoadIndustry && (!isIndustryFetched || isIndustryLoading)) ||
        !hasComparisonEntities));
  // The catalog template only takes companies, so the industry comparison
  // stays on composition_v3.
  const catalogDimension = isCatalogSegmentDimension(subfilterDimension)
    ? subfilterDimension
    : undefined;
  const usesCatalog =
    isDiversityQueryEnabled &&
    usesCompetitorComparison &&
    secondaryDimension === 'role' &&
    catalogDimension !== undefined &&
    competitorEntities !== undefined &&
    isDiversityCatalogSupported(competitorEntities);
  const catalogQuery = useDiversityCatalogData(primaryDimension, {
    entitiesOverride,
    pause: pauseCompositionQueries || !usesCatalog,
  });
  const breakdownIdOption = secondaryDimension
    ? breakdownIdOptionByDimension[secondaryDimension]
    : undefined;
  const topBreakdownEntitiesOverride = dynamic
    ? entitiesOverride?.slice(0, 1)
    : entitiesOverride;
  const topBreakdownQuery = useCompositionData(primaryDimension, {
    isTimeseries: false,
    entitiesOverride: topBreakdownEntitiesOverride,
    additionalEffectiveFilters,
    requireFullMetrics: false,
    pause: pauseCompositionQueries || usesCatalog || !hasActiveSubfilter,
  });
  const comparisonBreakdownIds =
    hasActiveSubfilter && secondaryDimension
      ? getPrimaryEntityBreakdownIds({
          data: topBreakdownQuery.compositionData ?? undefined,
          dimension: secondaryDimension,
        })
      : undefined;
  const comparisonBreakdownIdsReady =
    breakdownIdOption !== undefined &&
    topBreakdownQuery.status === DataProviderQueryStatus.Success &&
    comparisonBreakdownIds !== undefined &&
    comparisonBreakdownIds.length > 0;
  const comparisonNumeratorQuery = useCompositionData(primaryDimension, {
    isTimeseries: false,
    entitiesOverride,
    additionalEffectiveFilters,
    breakdownOptionsPatch: comparisonBreakdownIdsReady
      ? { [breakdownIdOption]: comparisonBreakdownIds }
      : undefined,
    requireFullMetrics: false,
    pause:
      pauseCompositionQueries ||
      usesCatalog ||
      !dynamic ||
      !hasActiveSubfilter ||
      !comparisonBreakdownIdsReady,
  });
  const numeratorQuery = dynamic ? comparisonNumeratorQuery : topBreakdownQuery;
  const denominatorQuery = useCompositionData(primaryDimension, {
    isTimeseries: false,
    entitiesOverride,
    breakdownOptionsPatch: comparisonBreakdownIdsReady
      ? { [breakdownIdOption]: comparisonBreakdownIds }
      : undefined,
    requireFullMetrics: false,
    pause:
      pauseCompositionQueries ||
      usesCatalog ||
      (subfilterable && !hasActiveSubfilter) ||
      (hasActiveSubfilter && !comparisonBreakdownIdsReady),
  });
  const status = (() => {
    if (usesCatalog) {
      return hasActiveSubfilter
        ? catalogQuery.status
        : DataProviderQueryStatus.Idle;
    }
    if (!hasActiveSubfilter) return denominatorQuery.status;
    if (
      topBreakdownQuery.status === DataProviderQueryStatus.Success &&
      comparisonBreakdownIds?.length === 0
    ) {
      return topBreakdownQuery.status;
    }
    return combineQueryStatus([
      topBreakdownQuery.status,
      ...(dynamic ? [comparisonNumeratorQuery.status] : []),
      denominatorQuery.status,
    ]);
  })();

  if (!secondaryDimension) {
    return (
      <ChartNotSupported>
        Configure a segment to use the diverging bar combo.
      </ChartNotSupported>
    );
  }

  if (dynamic && !focusCompany) {
    return (
      <ChartNotSupported>
        Select at least one company to compare.
      </ChartNotSupported>
    );
  }
  if (
    dynamic &&
    (isCompetitorsLoading ||
      (shouldLoadIndustry && (!isIndustryFetched || isIndustryLoading)))
  ) {
    return <ChartLoader />;
  }
  if (dynamic && hasCompetitorsError) {
    return <ChartFallback status={DataProviderQueryStatus.Error} />;
  }
  if (dynamic && !hasComparisonEntities) {
    return <ChartNotSupported>{INDUSTRY_UNAVAILABLE_ERROR}</ChartNotSupported>;
  }

  return (
    <div className={styles.dynamicRoot}>
      {subfilterable && subfilterDimension && (
        <ComboSubfilterDefaultSelection
          plotId={plotId}
          dimension={subfilterDimension}
        />
      )}
      <div className={styles.dynamicColumn}>
        <div className={styles.paneBody}>
          <ChartFallback status={status}>
            {usesCatalog && catalogDimension ? (
              <CatalogDivergingBarComboBody
                entities={catalogQuery.data}
                dimension={catalogDimension}
                segmentIds={getSelectedSegmentIds(selectedSegments)}
                hideOther={hideOther}
                selections={selections}
              />
            ) : (
              <DynamicDivergingBarComboBody
                numeratorData={
                  (hasActiveSubfilter
                    ? numeratorQuery.compositionData
                    : denominatorQuery.compositionData) ?? undefined
                }
                denominatorData={
                  hasActiveSubfilter
                    ? (denominatorQuery.compositionData ?? undefined)
                    : undefined
                }
                requiresDenominator={hasActiveSubfilter}
                dynamic={dynamic}
                aggregateCompetitors={usesCompetitorComparison}
                hideOther={hideOther}
                monthRefs={denominatorQuery.rawMonthRefs}
                secondaryDimension={secondaryDimension}
                selections={selections}
              />
            )}
          </ChartFallback>
        </div>
      </div>
    </div>
  );
};

const DynamicDivergingBarComboBody = ({
  numeratorData,
  denominatorData,
  requiresDenominator,
  dynamic,
  aggregateCompetitors,
  hideOther,
  monthRefs,
  secondaryDimension,
  selections,
}: {
  numeratorData: CompositionV3AnyGraphqlResponse | undefined;
  denominatorData: CompositionV3AnyGraphqlResponse | undefined;
  requiresDenominator: boolean;
  dynamic: boolean;
  aggregateCompetitors: boolean;
  hideOther: boolean;
  monthRefs: string[];
  secondaryDimension: CompositionSecondaryDimensionValues;
  selections: PrimarySelection[];
}) => {
  const convert = (data: CompositionV3AnyGraphqlResponse | undefined) =>
    data
      ? convertCompositionDataV3({
          metric: 'headcount',
          segment: secondaryDimension,
          data,
          isTimeseries: false,
          monthRefs,
        })
      : undefined;

  const numeratorConverted = convert(numeratorData);
  if (
    !numeratorConverted ||
    numeratorConverted.type !== DataType.BREAKDOWN_SINGLE
  ) {
    return <ChartNotSupported />;
  }

  const denominatorConverted = convert(denominatorData);
  const penetration =
    denominatorConverted?.type === DataType.BREAKDOWN_SINGLE
      ? denominatorConverted
      : undefined;

  if (requiresDenominator && !penetration) {
    return <ChartNotSupported />;
  }

  const combined = penetration
    ? buildPenetrationBreakdownSet(numeratorConverted, penetration)
    : numeratorConverted;

  return (
    <DivergingBarComboComparison
      dataSet={combined}
      dynamic={dynamic}
      aggregateCompetitors={aggregateCompetitors}
      hideOther={hideOther}
      selections={selections}
    />
  );
};

const CatalogDivergingBarComboBody = ({
  entities,
  dimension,
  segmentIds,
  hideOther,
  selections,
}: {
  entities: DiversityCatalogEntity[] | undefined;
  dimension: CatalogSegmentDimension;
  segmentIds: number[];
  hideOther: boolean;
  selections: PrimarySelection[];
}) => {
  const dataSet = entities
    ? buildCatalogPenetrationSet({ entities, dimension, segmentIds })
    : null;
  if (!dataSet) {
    return <ChartNotSupported />;
  }

  return (
    <DivergingBarComboComparison
      dataSet={dataSet}
      dynamic
      aggregateCompetitors
      hideOther={hideOther}
      selections={selections}
    />
  );
};

const DivergingBarComboComparison = ({
  dataSet,
  dynamic,
  aggregateCompetitors,
  hideOther,
  selections,
}: {
  dataSet: BreakdownSingleDataSet;
  dynamic: boolean;
  aggregateCompetitors: boolean;
  hideOther: boolean;
  selections: PrimarySelection[];
}) => {
  const converted = hideOther ? removeOtherSegments(dataSet) : dataSet;

  const comparisonData = aggregateCompetitors
    ? aggregateCompetitorShares(converted, COMPETITOR_AVERAGE_LABEL)
    : converted;

  const resolution = resolveComboEntities({
    converted: comparisonData,
    dynamic,
    selections,
    dynamicUnavailableError:
      aggregateCompetitors || !dynamic
        ? COMPETITOR_UNAVAILABLE_ERROR
        : INDUSTRY_UNAVAILABLE_ERROR,
  });

  if (!resolution.dataSet) {
    return <ChartNotSupported>{resolution.error}</ChartNotSupported>;
  }

  return <DivergingBarComboWithMenuData dataSet={resolution.dataSet} />;
};

// This combo fetches outside DataProvider, so it hands its data to the chart
// action menu itself; without it the menu and the page PPT export skip it.
const DivergingBarComboWithMenuData = ({
  dataSet,
}: {
  dataSet: BreakdownSingleDataSet;
}) => {
  const { setDataProviderSet } = useChartMenuContext();

  useEffect(() => {
    setDataProviderSet(dataSet);
  }, [dataSet, setDataProviderSet]);

  return (
    <DivergingBarCombo
      dataSet={dataSet}
      formatOverride={FormatType.PERCENTAGE}
    />
  );
};

// Seed the subfilter with a default selection
const ComboSubfilterDefaultSelection = ({
  plotId,
  dimension,
}: {
  plotId: string;
  dimension: CompositionSecondaryDimensionValues;
}) => {
  const { selectedSegments, setSelections } = useSegmentSelections(plotId);
  const { subfilterList, isTreeSubfilter, defaultSelection } =
    useShareOfSegmentSubfilters(dimension);
  const seededKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!subfilterList.length || !defaultSelection) return;

    const seedKey = `${dimension}:${defaultSelection.id}`;
    if (seededKeyRef.current === seedKey) return;
    seededKeyRef.current = seedKey;

    const hasValidSelections =
      selectedSegments.length > 0 &&
      areSelectionsValid(selectedSegments, subfilterList, isTreeSubfilter);

    if (!hasValidSelections) {
      setSelections([defaultSelection], { updateInitial: true });
    }
  }, [
    dimension,
    subfilterList,
    defaultSelection,
    isTreeSubfilter,
    selectedSegments,
    setSelections,
  ]);

  return null;
};
